import {CalendarIcon, RocketIcon, SingleSelectIcon} from '@primer/octicons-react'
import {Box, Octicon, Text} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type {ElementType, ReactNode} from 'react'

import type {MemexStatus} from '../../../api/memex/contracts'
import {formatISODateString} from '../../../helpers/parsing'
import {SanitizedMarkdownViewer} from '../../common/sanitized-markdown-viewer'
import {SingleSelectToken} from '../../fields/single-select/single-select-token'

type Props = {
  status: MemexStatus
}

const statusUpdateItemRowStyle: BetterSystemStyleObject = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 3,
  width: '100%',
  mt: 2,
}

const ItemDetailsRow = ({label, icon, children}: {label: string; icon: ElementType<any>; children: ReactNode}) => {
  return (
    <Box sx={statusUpdateItemRowStyle}>
      <Text
        as="p"
        sx={{
          fontWeight: 500,
          fontSize: 12,
          marginBottom: 0,
          wordBreak: 'break-word',
          width: '122px',
          flexShrink: 0,
          ml: 2,
        }}
      >
        <Octicon icon={icon} sx={{color: 'fg.subtle', mr: 2}} />
        {label}:
      </Text>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          fontSize: 12,
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

const statusUpdateItemBodyStyle: BetterSystemStyleObject = {
  flexGrow: 1,
  width: '100%',
  minWidth: 0,
}

export const StatusUpdateItemBody = ({status}: Props) => {
  const {startDate, targetDate, status: statusOption} = status.statusValue
  const {bodyHtml} = status

  return (
    <Box sx={statusUpdateItemBodyStyle}>
      {startDate && (
        <ItemDetailsRow label="Start date" icon={CalendarIcon}>
          {formatISODateString(new Date(startDate))}
        </ItemDetailsRow>
      )}
      {targetDate && (
        <ItemDetailsRow label="Target date" icon={RocketIcon}>
          {formatISODateString(new Date(targetDate))}
        </ItemDetailsRow>
      )}
      <ItemDetailsRow label="Status" icon={SingleSelectIcon}>
        <SingleSelectToken option={statusOption} />
      </ItemDetailsRow>
      <Box sx={{p: 2}}>
        <SanitizedMarkdownViewer dangerousRenderedHTML={{__html: bodyHtml}} loading={false} />
      </Box>
    </Box>
  )
}

try{ ItemDetailsRow.displayName ||= 'ItemDetailsRow' } catch {}
try{ StatusUpdateItemBody.displayName ||= 'StatusUpdateItemBody' } catch {}